import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface SortableCategoryItemProps {
  category: Category;
  isSelected: boolean;
  onSelect: (id: number) => void;
}

function SortableCategoryItem({ category, isSelected, onSelect }: SortableCategoryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto' as const,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
        isSelected
          ? 'bg-dark text-white'
          : 'bg-white text-dark hover:bg-warm-100'
      }`}
      onClick={() => onSelect(category.id)}
    >
      <button
        {...attributes}
        {...listeners}
        className={`w-6 h-6 flex items-center justify-center rounded cursor-grab active:cursor-grabbing flex-shrink-0 ${
          isSelected ? 'text-white/60 hover:text-white' : 'text-dark/30 hover:text-dark/60'
        }`}
        onClick={(e) => e.stopPropagation()}
        title="Arraste para reordenar"
      >
        <i className="ri-drag-move-2-line text-sm" />
      </button>
      <span className="flex-1 truncate select-none">{category.name}</span>
    </div>
  );
}

interface Props {
  categories: Category[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onReorder: (categories: Category[]) => void;
}

export default function SortableCategoryList({ categories, selectedId, onSelect, onReorder }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newCategories = arrayMove(categories, oldIndex, newIndex);
    onReorder(newCategories);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {categories.map((cat) => (
            <SortableCategoryItem
              key={cat.id}
              category={cat}
              isSelected={selectedId === cat.id}
              onSelect={onSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}